/**
 * AddressCoreFields — 7 pól core adresu (kraj/ulica/nr budynku/nr lokalu/kod
 * pocztowy/miasto/województwo), WSPÓLNE dla:
 *  - zakładki „Dane" (adres macierzysty kontrahenta) — `ContractorPrimaryAddressSection`,
 *  - modala „Nowy/Edycja adresu" w zakładce „Adresy" — `AddressesDynamicTab`.
 *
 * Przed tym komponentem oba miejsca miały osobne formularze z innymi etykietami
 * i bez bramki kraju — modal pozwalał wybrać Województwo nawet dla kraju ≠ PL.
 *
 * Bramka: Województwo widoczne i wybieralne TYLKO gdy kraj = Polska; zmiana
 * kraju na inny czyści i chowa Województwo (nie zostawiamy „sieroty").
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CoreInput } from '@/components/CoreInput';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import { SearchableSelect, type SearchableSelectOption } from '@/components/SearchableSelect';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { applyOrder } from '@/lib/kartoteki/formatka-order';

/** Kod kraju oznaczający Polskę — tylko wtedy pokazujemy Województwo. */
export const POLAND_COUNTRY_CODE = 'PL';

export interface AddressCoreFieldsValue {
  countryCode: string | null;
  street: string | null;
  buildingNo: string | null;
  apartmentNo: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
}

export type AddressCoreField = keyof AddressCoreFieldsValue;

/** Stała, sensowna kolejność pól core adresu w formatce: Kraj → Ulica → Nr budynku
 *  → Nr lokalu → Kod → Miejscowość → Województwo. */
export const ADDRESS_CORE_FIELD_ORDER: readonly AddressCoreField[] = [
  'countryCode',
  'street',
  'buildingNo',
  'apartmentNo',
  'postalCode',
  'city',
  'region',
];

/** i18n suffix (pod `tenant.addresses.coreField.*`) per pole — jedne etykiety dla całej apki. */
export const ADDRESS_CORE_FIELD_I18N_SUFFIX: Record<AddressCoreField, string> = {
  countryCode: 'countryCode',
  street: 'street',
  buildingNo: 'buildingNo',
  apartmentNo: 'apartmentNo',
  postalCode: 'postalCode',
  city: 'city',
  region: 'region',
};

const TEXT_MAXLEN: Partial<Record<AddressCoreField, number>> = {
  street: 200,
  buildingNo: 20,
  apartmentNo: 20,
  postalCode: 20,
  city: 100,
};

/** Bezpieczny odczyt `element.settings.hiddenFields` → lista prawidłowych kluczy pól core adresu. */
export function parseHiddenAddressCoreFields(
  settings: Record<string, unknown> | undefined,
): AddressCoreField[] {
  const raw = settings?.hiddenFields;
  if (!Array.isArray(raw)) return [];
  const valid = new Set<string>(ADDRESS_CORE_FIELD_ORDER as readonly string[]);
  return raw.filter((k): k is AddressCoreField => typeof k === 'string' && valid.has(k));
}

export interface AddressCoreFieldsProps {
  value: AddressCoreFieldsValue;
  onChange: (next: AddressCoreFieldsValue) => void;
  editable: boolean;
  /** Klucze pól ukrytych (np. przez konfigurację elementu w kreatorze). */
  hiddenFields?: ReadonlyArray<AddressCoreField>;
  /** Kolejność pól (slugi) z formatki — drag w builderze; nieujęte pola na końcu (back-compat). */
  fieldOrder?: ReadonlyArray<string>;
  /** Komunikaty błędów walidacji per pole (klucz = pole core), np. z odpowiedzi backendu. */
  errors?: Partial<Record<AddressCoreField, string>>;
  /** Generator `data-testid` per pole — default `address-core-field-<pole>`. Caller
   *  może nadpisać, żeby zachować własną konwencję (np. `${testIdPrefix}-form-<slug>`). */
  testId?: (field: AddressCoreField) => string;
}

export function AddressCoreFields({
  value,
  onChange,
  editable,
  hiddenFields = [],
  fieldOrder,
  errors,
  testId: testIdFor = (field) => `address-core-field-${field}`,
}: AddressCoreFieldsProps) {
  const { t } = useTranslation();

  // Słowniki globalne — pobierane raz (React Query dedupuje po kluczu, niezależnie od
  // liczby instancji tego komponentu na stronie).
  const countriesDict = useDictionary('countries');
  const wojewodztwaDict = useDictionary('wojewodztwa');

  const countryOptions = useMemo<SearchableSelectOption[]>(
    () =>
      countriesDict.activeItems.map((it) => ({
        value: it.code,
        label: formatDictionaryOptionLabel(it, countriesDict.dictionary?.displayMode ?? 'label'),
      })),
    [countriesDict.activeItems, countriesDict.dictionary?.displayMode],
  );
  const regionOptions = useMemo<SearchableSelectOption[]>(
    () =>
      wojewodztwaDict.activeItems.map((it) => ({
        value: it.code,
        label: formatDictionaryOptionLabel(it, wojewodztwaDict.dictionary?.displayMode ?? 'label'),
      })),
    [wojewodztwaDict.activeItems, wojewodztwaDict.dictionary?.displayMode],
  );

  const hidden = new Set(hiddenFields);
  // Kolejność z formatki (drag) — `applyOrder` układa wg `fieldOrder`, nieujęte na końcu.
  const orderedFields = applyOrder(
    ADDRESS_CORE_FIELD_ORDER,
    fieldOrder ?? [],
  ) as AddressCoreField[];
  const visibleFields = orderedFields.filter((field) => {
    if (hidden.has(field)) return false;
    // Województwo tylko dla Polski — bramka.
    if (field === 'region' && value.countryCode !== POLAND_COUNTRY_CODE) return false;
    return true;
  });

  return (
    <>
      {visibleFields.map((field) => {
        const label = t(`tenant.addresses.coreField.${ADDRESS_CORE_FIELD_I18N_SUFFIX[field]}`);
        const testId = testIdFor(field);
        const error = errors?.[field];

        let control: React.ReactNode;
        if (field === 'countryCode') {
          control = (
            <SearchableSelect
              value={value.countryCode}
              // Zmiana kraju na inny niż Polska czyści województwo (nie zostawiamy „sieroty").
              onChange={(v) =>
                onChange({
                  ...value,
                  countryCode: v,
                  region: v === POLAND_COUNTRY_CODE ? value.region : null,
                })
              }
              options={countryOptions}
              disabled={!editable}
              allowClear
              data-testid={testId}
            />
          );
        } else if (field === 'region') {
          control = (
            <SearchableSelect
              value={value.region}
              onChange={(v) => onChange({ ...value, region: v })}
              options={regionOptions}
              disabled={!editable}
              allowClear
              data-testid={testId}
            />
          );
        } else {
          control = (
            <CoreInput
              value={(value[field] as string | null) ?? ''}
              onChange={(v) => onChange({ ...value, [field]: v || null })}
              disabled={!editable}
              invalid={!!error}
              maxLength={TEXT_MAXLEN[field]}
              testId={testId}
            />
          );
        }

        return (
          <Field key={field} label={label} error={error}>
            {control}
          </Field>
        );
      })}
    </>
  );
}
